import React, { useState } from 'react'

import { useSelector } from 'react-redux'
import { formatTime } from '@/utils/helpers'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { Banner } from '@/components/banners/Banner'

import { determineAccessForUser, processTags } from '../helpers'
import { buildExistingValuesForUserForm, buildSchemaForUserForm } from '../organization.schema'
import { DeleteUserModal } from './DeleteUserModal'
import { BaltoCloudAccessModal } from './BaltoCloudAccessModal'

import './UserForm.scss'

export const UserForm = ({
  playbooks,
  handleSubmitUser,
  modalClose,
  qaEnabled,
  qaCopilotEnabled,
  agentGPTEnabled,
  realtimeCoachingAccessEnabled,
  orgDesktopEnterpriseEnabled,
  tagCategories,
  tags,
  user,
  flags,
  toolsEnabled,
}) => {
  const existingValues = user
    ? buildExistingValuesForUserForm(user, tagCategories)
    : { deleted: false, desktop_enterprise_enabled: false }
  const [isDeleted, setIsDeleted] = useState(false)
  const [hasBaltoCloudAccess, setHasBaltoCloudAccess] = useState(false)
  const [editedUser, setEditedUser] = useState(null)
  const { currentUser, organization } = useSelector((state) => state)
  const isBaltoAdmin = currentUser.organizationid === 1
  const isHierarchyManager = currentUser.hierarchy_manager
  const userPlaybook = user && playbooks.find((p) => p.cid === user.config_cid)
  const isPlaybookHidden = userPlaybook ? userPlaybook.config_agent_app_visibility === false : false
  const saveUser = async (userToSave) => {
    const savedUser = { ...userToSave }

    savedUser.id = user ? user.id : null
    savedUser.tags = processTags(userToSave, tags)

    const saved = await handleSubmitUser(savedUser)

    // Only an explicit failure keeps the form open; handlers that return
    // nothing close it as before.
    if (saved !== false) {
      modalClose()
    }
  }

  const isDeletingUser = isDeleted && !user?.deleted

  const handleSubmit = async (values, { setSubmitting }) => {
    const mutatedUser = determineAccessForUser(values)
    setEditedUser(mutatedUser)

    if (mutatedUser.deleted && !user?.deleted) {
      setIsDeleted((prevState) => !prevState)
    } else if (mutatedUser.cloud_access && !user?.password) {
      setHasBaltoCloudAccess((prevState) => !prevState)
    } else {
      // saveUser closes the modal once the save succeeds.
      await saveUser(mutatedUser)
      setSubmitting(false)
    }
  }

  const handleClose = (e) => {
    modalClose(e)
  }

  const schema = buildSchemaForUserForm(
    playbooks,
    qaEnabled,
    qaCopilotEnabled,
    realtimeCoachingAccessEnabled,
    tagCategories,
    tags,
    orgDesktopEnterpriseEnabled,
    flags,
    isBaltoAdmin,
    isHierarchyManager,
    agentGPTEnabled,
    organization.id,
    toolsEnabled,
    organization.outbound_campaigns_enabled,
    organization.togo_enabled,
    organization.webhooks_enabled,
    organization.screen_capture_access_enabled,
    Boolean(user?.newrez_complaints_enabled)
  )

  return (
    <>
      {isDeletingUser && <DeleteUserModal user={editedUser} deleteUser={saveUser} />}
      {hasBaltoCloudAccess && <BaltoCloudAccessModal user={editedUser} saveUser={saveUser} />}
      {!isDeletingUser && !hasBaltoCloudAccess && (
        <>
          {isPlaybookHidden && (
            <Banner data-testid="hidden-playbook-banner" style={{ marginTop: 0 }} critical>
              <p>
                Hidden playbook assigned to the user. This may result in user unable to access the
                playbook in agent app if midcall playbook switching is enabled.
              </p>
            </Banner>
          )}
          <AbstractForm
            schema={schema}
            buttonLabel="Save"
            onSubmit={handleSubmit}
            onClose={handleClose}
            existingValues={existingValues}
            // Submit is disabled while invalid, so `code` would never get touched
            // and its "required when Complaints Enabled" error would stay hidden.
            initialTouched={{ code: true }}
            isModal
          />
          {user && (
            <div data-testid="create-date" className="user-created-info">
              <div>
                <div>Created:</div>
                <div>by {user.created_by || 'Balto Admin'}</div>
                <div>on {user.created_on ? formatTime(user.created_on) : 'unknown date'}</div>
              </div>
              {user.last_modified_by && (
                <div data-testid="last-modified-date">
                  <div>Last Modified:</div>
                  <div>by {user.last_modified_by}</div>
                  <div>
                    on {user.last_modified_on ? formatTime(user.last_modified_on) : 'unknown date'}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  )
}

export default UserForm
